import { HttpService } from './http.service';
import { fetchAllPages, listQuery } from './listQuery';
import { http } from '@/services/http';
import { Config } from '@/app/constants/config';
import type { Club } from '@/common/model/types';
import type { BackendTeam, BackendTeamSelectionCandidate, PlayerSeasons, TeamPlayerAccess } from '@/types';

export const TeamService = {
  async getTeams(season?: string): Promise<BackendTeam[]> {
    return fetchAllPages((page) => HttpService.get<BackendTeam[]>(`/teams?${listQuery(season, page)}`));
  },

  async getPlayerAccess(): Promise<TeamPlayerAccess[]> {
    return HttpService.get<TeamPlayerAccess[]>('/teams/player-access');
  },

  async getPlayerSeasons(playerId: string): Promise<PlayerSeasons> {
    return HttpService.get<PlayerSeasons>(`/teams/players/${encodeURIComponent(playerId)}/seasons`);
  },

  async getTeam(id: string | number): Promise<BackendTeam> {
    return HttpService.get<BackendTeam>(`/teams/${id}`);
  },

  async createTeam(data: unknown): Promise<BackendTeam> {
    return HttpService.post<BackendTeam>('/teams', data);
  },

  async updateTeam(id: string | number, data: unknown): Promise<BackendTeam> {
    return http.put<BackendTeam>(`/teams/${id}`, data, { timeoutMs: Config.api.teamSaveTimeout, replayable: true });
  },

  async addPlayer(teamId: string | number, player: unknown): Promise<unknown> {
    return HttpService.post(`/teams/${teamId}/players`, player);
  },

  async updatePlayer(teamId: string | number, playerId: string | number, data: unknown): Promise<unknown> {
    return HttpService.put(`/teams/${teamId}/players/${playerId}`, data);
  },

  async deleteTeam(id: string | number): Promise<void> {
    await HttpService.delete<void>(`/teams/${id}`);
  },

  async deletePlayer(teamId: string | number, playerId: string | number): Promise<void> {
    await HttpService.delete<void>(`/teams/${teamId}/players/${playerId}`);
  },

  /** Teams counted in the plan quota (club teams, or own teams without a club). */
  async getActiveSelectionCandidates(): Promise<BackendTeamSelectionCandidate[]> {
    return HttpService.get<BackendTeamSelectionCandidate[]>('/teams/active-selection');
  },

  /**
   * Définit les équipes actives du compte (les autres passent en lecture seule).
   * Utilisé quand le compte dépasse le quota d'équipes de son plan.
   */
  async setActiveTeams(teamIds: Array<string | number>): Promise<BackendTeam[]> {
    return HttpService.post<BackendTeam[]>('/teams/active-selection', { teamIds });
  },

  async getClubs(): Promise<Club[]> {
    return HttpService.get<Club[]>('/teams/clubs');
  },

  async searchClubs(query: string): Promise<Club[]> {
    return HttpService.get<Club[]>(`/users/clubs/search?q=${encodeURIComponent(query)}`);
  },
};

